import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { authFetch } from '@/auth/token';
import {
  artifactFileUrl,
  downloadAllArtifacts,
  findOutputNode,
  flattenFiles,
  IFRAME_SANDBOX,
  parseCSV,
  parsePreviewUrl,
  primaryArtifact,
  shareUrlFromTree,
  hasInfraArtifacts,
  viewerKind,
  type OutputNode,
  type ViewerKind,
} from '../outputFiles';
import MarkdownPreview from './MarkdownPreview';
import DeployUpsell from './DeployUpsell';

const LANG_COLORS: Record<string, string> = {
  md: 'var(--accent)',
  json: 'var(--success)',
  ts: '#FDCB6E',
  js: '#FDCB6E',
  html: '#E17055',
  css: '#0984E3',
  image: '#00CEC9',
  pdf: '#D63031',
};

const fileIcon = (name: string, lang?: string) => {
  const kind = viewerKind(name, lang);
  if (kind === 'preview') return 'ri-earth-line';
  if (kind === 'html') return 'ri-html5-line';
  if (kind === 'table') return 'ri-table-line';
  if (kind === 'image') return 'ri-image-line';
  if (kind === 'pdf') return 'ri-file-pdf-line';
  if (kind === 'code') return 'ri-code-s-slash-line';
  if (name.endsWith('.md')) return 'ri-markdown-line';
  if (name.endsWith('.json')) return 'ri-braces-line';
  return 'ri-file-text-line';
};

async function downloadViaApi(outcomeId: string, path: string, name: string) {
  const res = await authFetch(artifactFileUrl(outcomeId, path));
  if (!res.ok) throw new Error('download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadNode(node: OutputNode, outcomeId?: string | null) {
  if (node.kind !== 'file') return;
  if (outcomeId && node.path) {
    void downloadViaApi(outcomeId, node.path, node.name).catch(() => {
      const blob = new Blob([node.content ?? ''], { type: 'text/plain;charset=utf-8' });
      triggerBlob(blob, node.name);
    });
    return;
  }
  const blob = new Blob([node.content ?? ''], { type: 'text/plain;charset=utf-8' });
  triggerBlob(blob, node.name);
}

function triggerBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function folderIds(nodes: OutputNode[]): string[] {
  const ids: string[] = [];
  const walk = (list: OutputNode[]) => {
    for (const n of list) {
      if (n.kind === 'folder') {
        ids.push(n.id);
        if (n.children) walk(n.children);
      }
    }
  };
  walk(nodes);
  return ids;
}

type Props = {
  tree: OutputNode[];
  focusId?: string | null;
  outcomeId?: string | null;
  outcomeSlug?: string | null;
};

export default function FileTree({ tree, focusId = null, outcomeId = null, outcomeSlug = null }: Props) {
  const allFiles = useMemo(() => flattenFiles(tree), [tree]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(folderIds(tree)));
  const [selectedId, setSelectedId] = useState<string>(allFiles[0]?.id ?? '');
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setExpanded(new Set(folderIds(tree)));
    const files = flattenFiles(tree);
    if (focusId && findOutputNode(tree, focusId)) setSelectedId(focusId);
    else {
      const primary = primaryArtifact(tree);
      setSelectedId(primary?.id ?? files[0]?.id ?? '');
    }
  }, [tree, focusId]);

  const selected = useMemo(() => findOutputNode(tree, selectedId), [tree, selectedId]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1600);
  };

  const toggleFolder = (id: string) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadAll = () => {
    if (outcomeId) {
      void downloadAllArtifacts(outcomeId, authFetch, triggerBlob)
        .then(() => showToast(`Downloading ${allFiles.length} files...`))
        .catch(() => showToast('Download failed'));
      return;
    }
    allFiles.forEach((f, i) => setTimeout(() => downloadNode(f, outcomeId), i * 350));
    showToast(`Downloading ${allFiles.length} files...`);
  };

  const shareUrl = useMemo(() => shareUrlFromTree(tree), [tree]);
  const showDeploy = useMemo(() => hasInfraArtifacts(tree), [tree]);
  const copyShare = () => {
    if (!shareUrl) return;
    void navigator.clipboard.writeText(shareUrl).then(
      () => showToast('Share link copied'),
      () => showToast('Copy failed'),
    );
  };

  const renderNode = (node: OutputNode, depth: number): ReactNode => {
    const pad = { paddingLeft: `${12 + depth * 16}px` };
    if (node.kind === 'folder') {
      const open = expanded.has(node.id);
      return (
        <div key={node.id}>
          <button
            onClick={() => toggleFolder(node.id)}
            className="w-full flex items-center gap-1.5 py-1.5 pr-2 text-left hover:bg-surface2 rounded-md"
            style={pad}
          >
            <i className={`${open ? 'ri-folder-open-line' : 'ri-folder-3-line'} text-texts text-sm w-4 flex items-center justify-center shrink-0`} />
            <span className="text-[13px] text-textp truncate flex-1">{node.name}</span>
          </button>
          {open && node.children?.map((c) => renderNode(c, depth + 1))}
        </div>
      );
    }

    const active = node.id === selectedId;
    return (
      <div key={node.id} className="group relative">
        <button
          onClick={() => setSelectedId(node.id)}
          className={`w-full flex items-center gap-1.5 py-1.5 pr-7 text-left rounded-md ${active ? 'bg-surface2' : 'hover:bg-surface2'}`}
          style={pad}
        >
          <i className={`${fileIcon(node.name, node.lang)} text-sm w-4 flex items-center justify-center shrink-0`} style={{ color: LANG_COLORS[node.lang ?? ''] ?? 'var(--texts)' }} />
          <span className="text-[13px] text-textp truncate flex-1">{node.name}</span>
          {node.size && <span className="text-[10px] font-mono text-texts shrink-0">{node.size}</span>}
        </button>
        <button
          onClick={() => downloadNode(node, outcomeId)}
          title="Download"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-texts hover:text-accent opacity-0 group-hover:opacity-100 transition-opacity rounded-md"
        >
          <i className="ri-download-2-line text-sm" />
        </button>
      </div>
    );
  };

  return (
    <div data-testid="file-tree" className="flex-1 flex flex-col overflow-hidden">
      <div className="shrink-0 px-5 py-3 border-b border-border flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-textp">Run artifacts</div>
          <div className="text-[11px] text-texts">
            {allFiles.length === 0
              ? 'No run artifacts yet'
              : `${allFiles.length} files from the last run`}
          </div>
        </div>
        {allFiles.length > 0 && (
          <div className="ml-auto shrink-0 flex items-center gap-2">
            {shareUrl && (
              <button
                type="button"
                onClick={copyShare}
                className="flex items-center gap-1.5 bg-surface2 hover:bg-border text-textp text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap"
              >
                <i className="ri-share-forward-line" /> Copy share link
              </button>
            )}
            {showDeploy && <DeployUpsell slug={outcomeSlug || outcomeId} outcomeId={outcomeId} />}
            <button
              onClick={downloadAll}
              className="flex items-center gap-1.5 bg-accent hover:bg-accent-hover text-accent-text text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap"
            >
              <i className="ri-download-line" /> Download all
            </button>
          </div>
        )}
      </div>

      {tree.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center text-texts px-6">
          <i className="ri-folder-3-line text-4xl mb-3" />
          <div className="text-sm text-textp font-medium">No run artifacts yet</div>
          <div className="text-xs mt-1 max-w-sm">
            Click Run. The frozen plan, step outputs, and git-tracked files will show up here.
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          <div className="w-72 shrink-0 border-r border-border overflow-y-auto p-2">
            {tree.map((n) => renderNode(n, 0))}
          </div>

          <div className="flex-1 flex flex-col overflow-hidden bg-surface/30">
            {selected && selected.kind === 'file' ? (
              <ArtifactPane node={selected} outcomeId={outcomeId} />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-texts">
                <i className="ri-file-list-3-line text-4xl mb-3" />
                <div className="text-sm text-textp font-medium">Select a file to preview</div>
                <div className="text-xs mt-1">Click a file in the tree to view its contents</div>
              </div>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 bg-textp text-bg text-xs font-medium px-3 py-1.5 rounded-md z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

function ArtifactPane({ node, outcomeId }: { node: OutputNode; outcomeId?: string | null }) {
  const kind = viewerKind(node.name, node.lang);
  const showToggle = kind === 'html' || kind === 'code' || kind === 'preview' || kind === 'markdown' || kind === 'table';
  const [mode, setMode] = useState<'preview' | 'source'>('preview');

  useEffect(() => {
    setMode('preview');
  }, [node.id]);

  return (
    <>
      <div className="shrink-0 px-4 py-2.5 border-b border-border flex items-center gap-2">
        <i className={`${fileIcon(node.name, node.lang)} text-sm`} style={{ color: LANG_COLORS[node.lang ?? ''] ?? 'var(--texts)' }} />
        <span className="text-[13px] font-medium text-textp">{node.name}</span>
        {node.size && <span className="text-[10px] font-mono text-texts">{node.size}</span>}
        {showToggle && (
          <div className="flex items-center gap-1 ml-2">
            <button
              type="button"
              onClick={() => setMode('preview')}
              className={`text-[11px] px-2 py-0.5 rounded-md ${mode === 'preview' ? 'bg-surface2 text-textp' : 'text-texts hover:text-textp'}`}
            >
              Preview
            </button>
            <button
              type="button"
              onClick={() => setMode('source')}
              className={`text-[11px] px-2 py-0.5 rounded-md ${mode === 'source' ? 'bg-surface2 text-textp' : 'text-texts hover:text-textp'}`}
            >
              Source
            </button>
          </div>
        )}
        <button
          onClick={() => downloadNode(node, outcomeId)}
          className="ml-auto flex items-center gap-1.5 bg-surface2 hover:bg-border text-textp text-xs px-2.5 py-1 rounded-md whitespace-nowrap"
        >
          <i className="ri-download-2-line" /> Download
        </button>
      </div>
      {mode === 'source' || kind === 'text' || kind === 'code' ? (
        <SourceView content={node.content} />
      ) : (
        <KindedViewer node={node} kind={kind} outcomeId={outcomeId} />
      )}
    </>
  );
}

function CSVTable({ content }: { content: string }) {
  const rows = parseCSV(content);
  if (!rows.length) return <SourceView content={content} />;
  const header = rows[0];
  const body = rows.slice(1);
  return (
    <div className="flex-1 overflow-auto p-4">
      <table className="text-[12px] text-textp border-collapse min-w-full">
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} className="border border-border bg-surface2 px-2 py-1 text-left font-medium whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri}>
              {header.map((_, ci) => (
                <td key={ci} className="border border-border px-2 py-1 align-top max-w-md">
                  {r[ci] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourceView({ content }: { content?: string }) {
  return (
    <pre className="flex-1 overflow-auto p-4 font-mono text-[12px] text-textp leading-relaxed whitespace-pre">
      {content || ''}
    </pre>
  );
}

function KindedViewer({ node, kind, outcomeId }: { node: OutputNode; kind: ViewerKind; outcomeId?: string | null }) {
  if (kind === 'preview') {
    const url = parsePreviewUrl(node.content);
    if (!url) return <MissingPreview label="Preview URL is missing" />;
    return <HostedFrame url={url} />;
  }
  if (kind === 'markdown') {
    return <MarkdownPreview content={node.content || ''} />;
  }
  if (kind === 'table') {
    return <CSVTable content={node.content || ''} />;
  }
  if (kind === 'html') {
    if (node.content && node.content.trim()) {
      return (
        <iframe
          title={node.name}
          sandbox={IFRAME_SANDBOX}
          srcDoc={node.content}
          className="flex-1 w-full bg-white"
        />
      );
    }
    return <FetchedHtml node={node} outcomeId={outcomeId} />;
  }
  if (kind === 'image' || kind === 'pdf') {
    return <BlobMedia node={node} kind={kind} outcomeId={outcomeId} />;
  }
  return <SourceView content={node.content} />;
}

function HostedFrame({ url }: { url: string }) {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="shrink-0 px-4 py-1.5 border-b border-border flex items-center gap-2">
        <a href={url} target="_blank" rel="noreferrer" className="text-[11px] text-accent hover:underline truncate">
          Open in new tab
        </a>
      </div>
      <iframe
        title="Static preview"
        sandbox={IFRAME_SANDBOX}
        src={url}
        className="flex-1 w-full bg-white"
      />
    </div>
  );
}

function MissingPreview({ label }: { label: string }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center text-texts px-6">
      <i className="ri-error-warning-line text-3xl mb-2" />
      <div className="text-sm text-textp">{label}</div>
    </div>
  );
}

function FetchedHtml({ node, outcomeId }: { node: OutputNode; outcomeId?: string | null }) {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!outcomeId || !node.path) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    authFetch(artifactFileUrl(outcomeId, node.path))
      .then(async (res) => {
        if (!res.ok) throw new Error('missing');
        const text = await res.text();
        if (!cancelled) setHtml(text);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [outcomeId, node.path]);

  if (failed || (!html && !outcomeId)) return <MissingPreview label="HTML is not available to preview" />;
  if (!html) return <MissingPreview label="Loading preview" />;
  return (
    <iframe
      title={node.name}
      sandbox={IFRAME_SANDBOX}
      srcDoc={html}
      className="flex-1 w-full bg-white"
    />
  );
}

function BlobMedia({ node, kind, outcomeId }: { node: OutputNode; kind: 'image' | 'pdf'; outcomeId?: string | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!outcomeId || !node.path) {
      setFailed(true);
      return;
    }
    let objectUrl = '';
    let cancelled = false;
    authFetch(artifactFileUrl(outcomeId, node.path))
      .then(async (res) => {
        if (!res.ok) throw new Error('missing');
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [outcomeId, node.path]);

  if (failed || !url) return <MissingPreview label={failed ? 'File is not available to preview' : 'Loading preview'} />;
  if (kind === 'image') {
    return <img src={url} alt={node.name} className="flex-1 object-contain p-4 max-h-full" />;
  }
  return <embed src={url} type="application/pdf" className="flex-1 w-full" />;
}
